import { sharedOptimizerApi } from '@fireflink/optimizer';
import {
  combineReducers,
  configureStore,
  Middleware,
  Tuple,
} from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  approvalVersionReducers,
  approvalVersionMiddlewares,
} from 'approvalVersion/store';
import posthog from 'posthog-js';
import hostExecutionDrawerSlice from 'service/hostExecutionDrawerSlice';

import { baseApi } from '@/lib/api/baseApi';

import { apiTrackerReducer } from './apiTracker/apiTrackerSlice';
import { environmentSliceReducer } from './EnvironmentConfiguration';
import { configurationDataReducer } from './EnvironmentConfiguration/ConfigurationDataSlice';
import { allMachinesDataReducer } from './EnvironmentConfiguration/EnvironmentConfigAllMachinesDataSlice';
import { projectDataReducer } from './EnvironmentConfiguration/ImpactListDataSlice';
import { executionSliceReducer } from './ExecutionConfig/ExecutionConfigSlice';
import resultConfigSlice from './ExecutionConfig/resultConfigSlice';
import globalLockSlice from './globalLock/globalLockSlice';
import selectedInstanceReducer from './integrations/selectedInstanceSlice';
import { rtkQueryErrorLogger } from './middleware/errorLogger';
import loadingMiddleware from './middleware/loadingMiddleware';
import { privilegeReducer } from './privilege/privilegeSlice';
import { projectManagementImpactListReducer } from './projectManagement/impactList/ImpactListDataSlice';
import { impactedUserNameReducer } from './projectManagement/usersManagement/projectManagementSlice';
import { impactListDetailsReducer } from './templates/templateSlice';
import defectManagementSlice from './userManagement/defectManagementSlice';
import memoryConfigSlice from './userManagement/memoryConfigSlice';
import { userProfileReducer } from './userProfile/userProfileSlice';

const hostReducer = {
  [baseApi.reducerPath]: baseApi.reducer,
  [sharedOptimizerApi.reducerPath]: sharedOptimizerApi.reducer,
  impactListDetails: impactListDetailsReducer,
  executionForm: executionSliceReducer,
  resultConfig: resultConfigSlice,
  machineDetailsForm: environmentSliceReducer,
  configurationData: configurationDataReducer,
  projectData: projectDataReducer,
  defectManagement: defectManagementSlice,
  projectManagement: projectManagementImpactListReducer,
  memoryConfig: memoryConfigSlice,
  globalLock: globalLockSlice,
  apiTracker: apiTrackerReducer,
  privilege: privilegeReducer,
  hostExecutionDrawer: hostExecutionDrawerSlice,
  impactedUserName: impactedUserNameReducer,
  allMachinesData: allMachinesDataReducer,
  profileDataSlice: userProfileReducer,
  selectedInstance: selectedInstanceReducer,
};

const rootReducer = combineReducers({
  ...hostReducer,
  ...approvalVersionReducers,
});
const isLocalhost =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';
const isOnprem = import.meta.env.VITE_ENVIRONMENT === 'onprem';
const enablePosthog = import.meta.env.VITE_ENABLE_POSTHOG === 'true';
// 1. Refined PostHog Middleware
const automaticPostHogMiddleware: Middleware = () => next => (action: any) => {
  if (isLocalhost || isOnprem || !enablePosthog) {
    return next(action);
  }
  // Only track if NOT in local development and action has a type
  if (action.type) {
    // Filter out internal Redux actions and noisy RTK Query background states
    const isInternalAction = action.type.startsWith('@@');
    const isRtkQueryAction = action.type.startsWith(`${baseApi.reducerPath}/`);

    if (!isInternalAction && !isRtkQueryAction) {
      if (action.type === 'login/setPassword') {
        posthog.capture('redux_action', {
          action_type: action.type,
          status: 'redacted for privacy',
        });
      } else {
        posthog.capture('redux_action', {
          action_type: action.type,
        });
      }
    }
  }

  return next(action);
};
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware): Tuple<Middleware[]> =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST'],
      },
    }).concat([
      baseApi.middleware,
      rtkQueryErrorLogger,
      // privilegeApi.middleware,
      loadingMiddleware,
      ...approvalVersionMiddlewares,
      automaticPostHogMiddleware,
      sharedOptimizerApi.middleware,
    ]),
  devTools: import.meta.env.MODE !== 'production',
});

window.addEventListener('privilegeStateUpdate', ((event: Event) => {
  const customEvent = event as CustomEvent;
  const action = customEvent.detail;
  if (
    action &&
    typeof action.type === 'string' &&
    action.type.startsWith('privilege/')
  ) {
    store.dispatch(action);
  }
}) as EventListener);

setupListeners(store.dispatch);

export type RootState = { [key: string]: any };
export type AppDispatch = typeof store.dispatch;
